import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_URL } from '@/config/site';

interface MetaTagsProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
}

const DEFAULT_META = {
  title: 'Star Chores - Reward Tracking App for Kids',
  description: 'A beautiful reward tracking app for children to earn stars for completing tasks. Build positive habits with customizable tasks, rewards, and achievements.',
  image: '/android-chrome-512x512.png', // App icon for Open Graph
  url: SITE_URL
};

export const MetaTags: React.FC<MetaTagsProps> = ({ 
  title = DEFAULT_META.title,
  description = DEFAULT_META.description,
  image = DEFAULT_META.image,
  url
}) => {
  const location = useLocation();
  const fullUrl = url || `${DEFAULT_META.url}${location.pathname}`;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }

    // Update Open Graph tags
    const updateMetaTag = (property: string, content: string) => {
      let metaTag = document.querySelector(`meta[property="${property}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    };

    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:image', image.startsWith('http') ? image : `${DEFAULT_META.url}${image}`);
    updateMetaTag('og:url', fullUrl);
    updateMetaTag('og:type', 'website');
    updateMetaTag('og:site_name', 'Star Chores');

    // Update Twitter Card tags
    const updateTwitterTag = (name: string, content: string) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('name', name);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    };

    updateTwitterTag('twitter:card', 'summary_large_image');
    updateTwitterTag('twitter:title', title);
    updateTwitterTag('twitter:description', description);
    updateTwitterTag('twitter:image', image.startsWith('http') ? image : `${DEFAULT_META.url}${image}`);

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', fullUrl);

  }, [title, description, image, fullUrl]);

  return null; // This component doesn't render anything
};

// Hook to get page-specific meta data
export const usePageMeta = (pathname: string) => {
  switch (pathname) {
    case '/':
      return {
        title: 'Star Chores - Reward Tracking Dashboard',
        description: 'Track your daily tasks, earn stars, and claim rewards. Build positive habits with our family-friendly reward system.',
      };
    case '/history':
      return {
        title: 'Star History - Star Chores',
        description: 'View your star earning history and track progress over time. See daily achievements and maintain your streaks.',
      };
    case '/trophies':
      return {
        title: 'Trophy Collection - Star Chores', 
        description: 'Explore your achievement collection and unlock new trophies. Celebrate milestones and completed challenges.',
      };
    default:
      if (pathname.startsWith('/edit/')) {
        return {
          title: 'Edit Mode - Star Chores',
          description: 'Customize your tasks, rewards, and achievements. Parent-controlled editing mode for family setup.',
        };
      }
      return {
        title: 'Star Chores - Reward Tracking App',
        description: 'A beautiful reward tracking app for children to earn stars for completing tasks.',
      };
  }
};